// @file Static Tldraw AI options.
import { captureException } from '@@/bits/error_tracker'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { ShapeDescriptions } from '@@/bits/tldraw/ai/transforms/ShapeDescriptions'
import { SimpleCoordinates } from '@@/bits/tldraw/ai/transforms/SimpleCoordinates'
import { SimpleIds } from '@@/bits/tldraw/ai/transforms/SimpleIds'
import { getScreenshotOfShapeIds } from '@@/bits/whiteboard_ai_logger'
import { createAssetForSticker } from '@@/bits/whiteboard_upload'
import { HttpCode } from '@@/enums'
import { useWhiteboardAiStore } from '@@/pinia/whiteboard_ai'
import type { CreateShapesPayload } from '@@/surface/api/whiteboard_ai'
import { useTldrawError } from '@@/vuecomposables/useTldrawError'
import { useWhiteboardConnectionUri } from '@@/vuecomposables/useWhiteboardConnectionUri'
import type { TLAiChange, TLAiResult, TLAiSerializedPrompt, TLAiTextMessage, TldrawAiOptions } from '@tldraw/ai'
import type { Editor, TLShape } from 'tldraw'

type AiStreamEvent =
  | {
      type: 'change'
      change: TLAiChange
    }
  | {
      type: 'message'
      message: string
    }

async function populatePrompt({
  editor,
  prompt,
}: {
  editor: Editor
  prompt: TLAiSerializedPrompt
}): Promise<TLAiSerializedPrompt> {
  try {
    /**
     * [
     *  {
     *    "type": "text",
     *    "text": "{\"message\":\"create a geo shape\",\"sound_effects\":{},\"visual_effects\":{},\"stickers\":{}}"
     *  }
     * ]
     */
    const message = prompt.message[0] as TLAiTextMessage
    const messageJson = JSON.parse(message.text) as CreateShapesPayload

    // Extract shape IDs from canvas content if available
    const shapeIds = Array.from(editor.getCurrentPageShapeIds())

    // Get screenshot URL for the canvas shapes
    let screenshotUrl = ''
    if (shapeIds.length > 0) {
      screenshotUrl = await getScreenshotOfShapeIds({
        userMessage: messageJson.message,
        shapeIds,
      })
    }

    const meta = {
      // Attach the selected shapes to the prompt
      selectedShapes: editor.getSelectedShapes(),
      soundEffects: messageJson.sound_effects,
      visualEffects: messageJson.visual_effects,
      stickers: messageJson.stickers,
    }

    return {
      ...prompt,
      message: [
        {
          type: 'text',
          text: messageJson.message,
        },
      ],
      image: screenshotUrl, // override the base64 image generated by tldraw/ai
      meta: {
        ...prompt.meta,
        ...meta,
      },
    }
  } catch (error) {
    captureException(error, { context: 'StaticTldrawAiOptionsPopulatePrompt' })
    return prompt
  }
}

function handleError(error: any): void {
  if (error.status === HttpCode.Unauthorized) {
    useTldrawError().showErrorSnackbar()
  } else {
    captureException(error, { context: 'StaticTldrawAiOptions' })
  }
}

const STATIC_TLDRAWAI_OPTIONS: TldrawAiOptions = {
  // Transforms that will be applied to the prompt before it's
  // sent and to changes as they're received.
  // TODO: add StripPadletPermissionsMeta
  transforms: [SimpleIds, ShapeDescriptions, SimpleCoordinates],
  // A function that calls the backend and return generated changes.
  generate: async ({ editor, prompt, signal }) => {
    try {
      const { getAiGenerateUri } = useWhiteboardConnectionUri()
      const populatedPrompt = await populatePrompt({ editor, prompt })
      const res = await fetch(await getAiGenerateUri(), {
        method: 'POST',
        body: asciiSafeStringify(populatedPrompt),
        headers: {
          'Content-Type': 'application/json',
        },
        signal,
      })

      const result: TLAiResult = await res.json()

      return result.changes
    } catch (error) {
      handleError(error)
      return []
    }
  },
  // A function similar to `generate` but that will stream changes from
  stream: async function* ({
    editor,
    prompt,
    signal,
  }: {
    editor: Editor
    prompt: TLAiSerializedPrompt
    signal: AbortSignal
  }) {
    const { getAiStreamUri } = useWhiteboardConnectionUri()
    const populatedPrompt = await populatePrompt({ editor, prompt })
    const res = await fetch(await getAiStreamUri(), {
      method: 'POST',
      body: asciiSafeStringify(populatedPrompt),
      headers: {
        'Content-Type': 'application/json',
      },
      signal,
    })

    if (res.status !== HttpCode.Ok) {
      handleError(res)
      return
    }

    if (res.body == null) {
      throw Error('No body in response')
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const events = buffer.split('\n\n')
        buffer = events.pop() ?? ''

        for (const event of events) {
          const match = event.match(/^data: (.+)$/m)
          if (match != null) {
            try {
              const change: AiStreamEvent = JSON.parse(match[1])
              if (change.type === 'change') {
                if (change.change.type === 'createShape' && 'shape' in change.change) {
                  const shape = change.change.shape as TLShape
                  useWhiteboardAiStore().newlyCreatedShapes.push(shape)

                  // Create the asset for the sticker if it doesn't exist
                  if (
                    shape.type === 'image' &&
                    'assetId' in shape.props &&
                    shape.props.assetId != null &&
                    typeof shape.meta.stickerUrl === 'string' &&
                    shape.meta.stickerUrl !== ''
                  ) {
                    createAssetForSticker({
                      assetId: shape.props.assetId,
                      url: shape.meta.stickerUrl,
                    })
                  }
                }

                yield change.change
              } else {
                // type: 'message'. No-ops
              }
            } catch (err) {
              throw Error(`JSON parsing error: ${match[1]}`)
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }
  },
}

export { STATIC_TLDRAWAI_OPTIONS }
